import { z } from 'zod';

const OptionalText = (max: number) => z.string().trim().min(1).max(max).optional();
const AttachmentLinksSchema = z.array(z.string().url().max(2048).refine((value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch { return false; }
}, 'Attachment links must be credential-free HTTPS URLs')).max(5);

/** Public beta-report payload. Deliberately excludes arbitrary logs, prompts and paths. */
export const BetaFeedbackRequestSchema = z.object({
  reportId: z.string().uuid().optional(),
  kind: z.enum(['bug', 'feedback']).default('feedback'),
  title: z.string().trim().min(3).max(160).optional(),
  message: z.string().trim().min(3).max(5000),
  steps: OptionalText(5000),
  expected: OptionalText(2000),
  area: z.string().trim().min(1).max(80).optional(),
  severity: z.enum(['blocking', 'major', 'minor']).optional(),
  contact: z.string().trim().email().max(320).optional(),
  diagnostics: z.object({
    app: z.object({ version: z.string().trim().max(100).optional(), shell: z.string().trim().max(80).optional() }).nullish().transform((value) => value ?? undefined),
    platform: z.string().trim().max(80).optional(),
    arch: z.string().trim().max(40).optional(),
    services: z.array(z.object({ name: z.string().trim().min(1).max(80), state: z.enum(['ready', 'degraded', 'unavailable', 'unknown']).catch('unknown') })).max(20).nullish().transform((value) => value ?? undefined),
  }).nullish().transform((value) => value ?? undefined),
  attachmentLinks: AttachmentLinksSchema.optional(),
}).strict();

export const BetaFeedbackResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    reportId: z.string().uuid(),
    status: z.enum(['pending', 'submitted']),
    issueUrl: z.string().url().refine((value) => {
      try {
        const url = new URL(value);
        return url.protocol === 'https:' && url.hostname === 'github.com' && !url.username && !url.password && /^\/[^/]+\/[^/]+\/issues\/\d+$/.test(url.pathname);
      } catch { return false; }
    }).optional(),
    issueNumber: z.number().int().positive().optional(),
    delivered: z.boolean(),
  }).refine((value) => value.delivered === (value.status === 'submitted'), 'Delivery status is inconsistent'),
});

export type BetaFeedbackRequest = z.infer<typeof BetaFeedbackRequestSchema>;
export type BetaFeedbackResponse = z.infer<typeof BetaFeedbackResponseSchema>;
